import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Grid,
} from "@mui/material";
import "./Registartion.css";
import { ProfileCreate, getCompanyProfile } from "../Services/Services";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import Link from "@mui/material/Link";

const logo = "/sid_logo.png"

const RegistrationPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone_no: "",
    address: "",
    password: "",
    confirmPassword: "",
  });
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState("");

  const [logoUrl, setLogoUrl] = useState(logo);

  useEffect(() => {
    const fetchCompanyLogo = async () => {
      try {
        const data = await getCompanyProfile();
        if (data && data.logo_url) {
          setLogoUrl(data.logo_url);
        }
      } catch (error) {
        console.warn("Could not load company logo, using default.");
      }
    };
    fetchCompanyLogo();
  }, []);

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === "email") {
      if (!validateEmail(value)) {
        setEmailError("Please enter a valid email.");
      } else {
        setEmailError("");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { name, email, phone_no, address, password, confirmPassword } =
      formData;

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email");
      toast.warning("⚠️ Please enter a valid email");
      return;
    }

    if (password !== confirmPassword) {
      toast.error(" Passwords do not match!");
      return;
    }

    setLoading(true);

    const payload = {
      name,
      email,
      phone_no,
      address,
      password,
      confirmPassword,
    };

    try {
      const response = await ProfileCreate(payload);

      toast.success("Registered successfully!");

      setFormData({
        name: "",
        email: "",
        phone_no: "",
        address: "",
        password: "",
        confirmPassword: "",
      });

      setTimeout(() => {
        navigate("/");
      }, 2000);
    } catch (err) {
      console.error(" Registration Failed:", err);

      if (err.response && err.response.status === 409) {
        toast.error("⚠️ This email is already registered!");
      } else {
        toast.error("Registration failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Input styles – white background, dark text, dark labels
  const inputStyle = {
    "& .MuiOutlinedInput-root": {
      color: "#0f172a",
      backgroundColor: "#ffffff !important",
      borderRadius: "12px",
      transition: "all 0.3s ease-in-out",
      "& input": {
        // 16px stops iOS Safari from auto-zooming the page on focus
        fontSize: "16px",
        padding: { xs: "13px 14px", sm: "16.5px 14px" },
      },
      "& fieldset": {
        borderColor: "rgba(0, 0, 0, 0.2)",
      },
      "&:hover fieldset": {
        borderColor: "#f9a51c",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#f9a51c",
        boxShadow: "0 0 12px rgba(249, 165, 28, 0.35)",
      },
    },
    "& .MuiInputLabel-root": { color: "rgba(0, 0, 0, 0.7)", fontSize: "0.95rem" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#f9a51c" },
    "& .MuiFormHelperText-root": { color: "#d32f2f" },
  };

  const BG_IMAGE_URL =
    "https://images.pexels.com/photos/7867332/pexels-photo-7867332.jpeg";

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: { xs: "max(12px, env(safe-area-inset-left))", sm: 2 },
        py: { xs: "max(16px, env(safe-area-inset-top))", sm: 4 },
        boxSizing: "border-box",
        backgroundImage: `radial-gradient(circle at 50% 20%, rgba(249, 165, 28, 0.15) 0%, transparent 60%), linear-gradient(135deg, rgba(12, 16, 26, 0.92) 0%, rgba(22, 28, 45, 0.95) 100%), url(${BG_IMAGE_URL})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: { xs: "scroll", md: "fixed" },
        position: "relative",
        overflowX: "hidden",
        overflowY: "auto",
      }}
    >
      <ToastContainer position="top-right" autoClose={2000} theme="dark" />

      {/* ✅ White Card */}
      <Box
        sx={{
          borderRadius: { xs: "18px", sm: "24px" },
          p: { xs: 2.5, sm: 4 },
          width: { xs: "100%", sm: "620px" },
          maxWidth: "100%",
          backgroundColor: "#ffffff",
          boxShadow: "0 20px 50px rgba(0, 0, 0, 0.6)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          position: "relative",
          zIndex: 1,
          border: "1px solid rgba(255,255,255,0.2)",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: { xs: 1.2, sm: 1.5 },
            borderRadius: "16px",
            backgroundColor: "#f8f9fa",
            border: "1px solid rgba(0, 0, 0, 0.08)",
            mb: { xs: 1.5, sm: 2 },
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            component="img"
            src={logoUrl}
            alt="Logo"
            sx={{
              width: { xs: 130, sm: 150, md: 170 },
              height: "auto",
              maxHeight: { xs: 72, sm: 82, md: 90 },
              objectFit: "contain",
              filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
            }}
            onError={(e) => {
              e.target.src = logo;
            }}
          />
        </Box>

        <Typography
          variant="h4"
          align="center"
          className="register-title"
          sx={{
            mb: { xs: 2, sm: 3 },
            fontWeight: 700,
            color: "#0f172a",
            letterSpacing: "0.5px",
            fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.125rem" },
          }}
        >
          Create Account
        </Typography>

        <form
          onSubmit={handleSubmit}
          className="register-form"
          style={{ width: "100%" }}
          noValidate
        >
          <Grid container spacing={{ xs: 1.75, sm: 2.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Name"
                name="name"
                fullWidth
                required
                value={formData.name}
                onChange={handleChange}
                sx={inputStyle}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                name="email"
                type="email"
                fullWidth
                required
                inputProps={{ inputMode: "email" }}
                value={formData.email}
                onChange={handleChange}
                error={!!emailError}
                helperText={emailError}
                sx={inputStyle}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone Number"
                name="phone_no"
                fullWidth
                required
                value={formData.phone_no}
                onChange={handleChange}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  maxLength: 10,
                }}
                onInput={(e) => {
                  e.target.value = e.target.value.replace(/[^0-9]/g, "");
                }}
                sx={inputStyle}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Address"
                name="address"
                fullWidth
                required
                value={formData.address}
                onChange={handleChange}
                sx={inputStyle}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Password"
                name="password"
                type="password"
                fullWidth
                required
                value={formData.password}
                onChange={handleChange}
                sx={inputStyle}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Confirm Password"
                name="confirmPassword"
                type="password"
                fullWidth
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                sx={inputStyle}
              />
            </Grid>

            <Grid item xs={12} sx={{ mt: { xs: 0.5, sm: 1 } }}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                className="register-button"
                sx={{
                  py: { xs: 1.3, sm: 1.6 },
                  width: "100%",
                  fontWeight: 700,
                  fontSize: { xs: "0.95rem", sm: "1rem" },
                  letterSpacing: "0.5px",
                  borderRadius: "12px",
                  backgroundColor: "#f9a51c !important",
                  color: "#121622",
                  boxShadow: "0 6px 20px rgba(249, 165, 28, 0.4)",
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    backgroundColor: "#ffb43a !important",
                    boxShadow: "0 8px 25px rgba(249, 165, 28, 0.6)",
                    transform: "translateY(-2px)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Register"
                )}
              </Button>

              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Link
                  href="/"
                  sx={{
                    color: "#0f172a",
                    fontSize: { xs: "0.85rem", sm: "0.95rem" },
                    fontWeight: 600,
                    textDecoration: "none",
                    transition: "color 0.2s ease",
                    "&:hover": {
                      color: "#f9a51c",
                      textDecoration: "underline",
                    },
                  }}
                >
                  Already have an account? Login
                </Link>
              </Box>
            </Grid>
          </Grid>
        </form>

        <Box
          sx={{
            mt: { xs: 2, sm: 3 },
            pt: 2,
            borderTop: "1px solid rgba(0, 0, 0, 0.1)",
            textAlign: "center",
            width: "100%",
          }}
          className="powered-box"
        >
          <Typography
            variant="body2"
            sx={{
              color: "#0f172acf",
              letterSpacing: 0.5,
              fontSize: { xs: "0.75rem", sm: "0.82rem" },
            }}
          >
            Powered by{" "}
            <Box
              component="span"
              sx={{
                fontWeight: "bold",
                "& a": {
                  color: "#d97706",
                  textDecoration: "none",
                  borderBottom: "1px solid rgba(249, 165, 28, 0.4)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    color: "#d97706",
                    borderColor: "#d97706",
                  },
                },
              }}
            >
              <a
                href="https://www.yashvitech.com"
                target="_blank"
                rel="noreferrer"
              >
                Yashvitech IT Solution Pvt. Ltd.
              </a>
            </Box>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default RegistrationPage;